import { useUser } from '@clerk/nextjs';
import { DailyCustomTrayButtons, DailyParticipant } from '@daily-co/daily-js';
import {
  useDaily,
  useMeetingState,
  useParticipantIds,
} from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

/**
 * Check if a participant is a PSTN dial-out participant.
 * Uses the participantType field added in daily-js 0.75.0.
 */
const isPstnDialOut = (p: DailyParticipant | null): boolean => {
  if (!p) return false;
  // participantType can be: 'sip-dial-in', 'sip-dial-out', 'pstn-dial-in', 'pstn-dial-out'
  return (
    (p as DailyParticipant & { participantType?: string }).participantType ===
    'pstn-dial-out'
  );
};

/**
 * CustomTrayButtons component manages Daily.co custom tray buttons.
 * - Adds endSession button for providers
 * - Adds dialOutPhone button for providers (dial-out is enabled on-demand when clicked)
 *   - When a dial-out is active, the button changes to "Hang Up" to end the call
 * - Adds leaveSession button for patients
 *
 * This component should be placed inside DailyProvider to have access to useDaily hook.
 */
export const CustomTrayButtons: React.FC = () => {
  const { user } = useUser();
  const daily = useDaily();
  const meetingState = useMeetingState();
  const isProvider = !!user;
  const router = useRouter();
  const roomId =
    typeof router.query.roomId === 'string' ? router.query.roomId : '';

  // Get all participant IDs to trigger re-render when participants change
  const participantIds = useParticipantIds();

  // Check if there's an active PSTN dial-out participant
  const hasActiveDialOut = useMemo(() => {
    if (!daily) return false;
    const participants = daily.participants();
    return Object.values(participants).some(isPstnDialOut);
  }, [daily, participantIds]);

  /**
   * Set up custom tray buttons
   * IMPORTANT: updateCustomTrayButtons completely replaces all buttons,
   * so the entire button config goes here.
   */
  useEffect(() => {
    if (!daily || meetingState !== 'joined-meeting') return;
    const buttons: DailyCustomTrayButtons = {};

    // Include dial-out and endSession buttons for providers
    if (isProvider) {
      if (hasActiveDialOut) {
        // Hang up icon when dial-out is active (phone with X)
        buttons.dialOutPhone = {
          iconPath:
            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23EF473B"%3E%3Cpath d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/%3E%3C/svg%3E',
          label: 'Hang Up',
          tooltip: 'End the phone call',
        };
      } else {
        // Rotary phone icon for dial-out (from game-icons.net, CC BY 3.0)
        buttons.dialOutPhone = {
          iconPath:
            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="%2338A169"%3E%3Cpath d="M256 92.6c-69.6-.1-139.1 11.6-208.56 35.4 0 0-9.87 22.6-17.98 41.3a46.9 46.9 0 0 0-3.49 12.2c48.55-18.4 97.13-31 145.63-38 4.5-8.8 13.7-14.6 23.9-14.6h17.3c7.9 0 15.1 3.4 20.1 8.9 15.4-.6 30.8-.7 46.4-.1 4.9-5.5 12.1-8.8 19.8-8.8h17.3c10.1 0 19.1 5.6 23.7 14.1 48.7 6.6 97.2 19.4 145.9 38.3-.7-4-1.8-8.2-3.5-12-8.1-18.7-18-41.3-18-41.3-69.5-23.4-139-35.33-208.5-35.4zm-60.5 53.6c-4.7 0-8.8 3.4-9.6 8.1l-6.7 40.4c-12.1 2.2-23.9 5-35.3 8.4-9.8 3-17.6 10.4-21.2 19.9-13.8 37-48.72 130.6-48.72 130.6H438s-34.8-93.5-48.7-130.6c-3.5-9.5-11.4-17-21.2-19.9-11.4-3.4-23.2-6.2-35.3-8.4l-6.8-40.4c-.8-4.7-4.8-8.1-9.6-8.1h-17.3c-4.6 0-8.7 3.2-9.5 7.8l-2.2 10.6h-62.9l-2.2-10.6c-.8-4.6-4.8-7.8-9.5-7.8zm163.8 17.3-2.7 18.6c5.6 1.4 11.1 2.8 16.4 4.5 14 6.2 25.7 15.7 31.1 27.2l64.8 15.5c6.4-9.8 13.6-18.7 16.3-29.8-42-16.6-83.9-28.7-125.9-36zm-206.5.5c-42 7.5-84.01 19.4-126.02 35.6 3.05 11.7 9.6 19.6 16.31 29.7l64.81-15.5c5.9-13 17.2-23 31-27.2 5.5-1.6 11-3.1 16.5-4.5zM256 197.3c44.7 0 81 30.9 81 69s-36.3 68.9-81 68.9-81.1-30.8-81.1-68.9c0-38.1 36.4-69 81.1-69zm0 17.6c-30.1 0-54.5 20.8-54.5 46.4 0 25.6 24.4 46.4 54.5 46.4s54.4-20.8 54.4-46.4c0-25.6-24.3-46.4-54.4-46.4zM73.97 376.5l21.76 42.9H416.2l21.7-42.9z"/%3E%3C/svg%3E',
          label: 'Call Patient',
          tooltip: 'Call patient by phone',
        };
      }

      buttons.endSession = {
        iconPath:
          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23EF473B"%3E%3Cpath d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/%3E%3C/svg%3E',
        label: 'End Session',
        tooltip: 'End the session for everyone',
      };
    } else {
      buttons.leaveSession = {
        iconPath:
          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23EF473B"%3E%3Cpath d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/%3E%3C/svg%3E',
        label: 'Leave Session',
        tooltip: 'Leave the session',
      };
    }

    try {
      daily.updateCustomTrayButtons(buttons);
    } catch (err) {
      datadogLogs.logger.error(
        'Error updating custom tray buttons',
        callLogContext('tray-buttons', {
          roomId,
        }),
        err instanceof Error ? err : undefined,
      );
    }
  }, [daily, isProvider, meetingState, hasActiveDialOut, roomId]);

  return null;
};
